import { ReactNode, useMemo, useRef } from 'react';

import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import {
  useGetInteractiveLessonDataFromCache,
  useGetInteractiveLessonParams,
} from 'approot/activity-builder/activity-builder.hooks';
import { ViewProps } from './vocabulary.constants';
import VocabularyViewModal from './vocabulary-view-modal';
import { LANGUAGE_CODE_ES_US, LESSON_ACTIVITIES } from 'global/constants';
import LessonSpanishSwitch from 'approot/lesson/lesson-spanish-switch';
import { useRegionData } from 'approot/shared/region/region.apollo';
import * as Styled from './vocabulary-view.style';
import { isLanguageAvailable } from './vocabulary.utils';

const VocabularyView = ({
  vocabularyDefinitions,
  onComponentExpand,
  includeToggle = true,
  sectionId,
  id: componentId,
  isStudent,
}: ViewProps & {
  isStudent?: boolean;
  onComponentExpand?: (data: {
    vocabularyWord: string;
    vocabularyWordCount: number;
  }) => void;
  includeToggle?: ReactNode;
}) => {
  const indexRef = useRef<number>(0);
  const { isRegionUS } = useRegionData();

  const { contentId, contentType } = useGetInteractiveLessonParams();

  const { getInteractiveContent, getInteractiveAssessmentContent } =
    useGetInteractiveLessonDataFromCache();

  const isSpanishSupported = useMemo(
    () => isLanguageAvailable(vocabularyDefinitions, LANGUAGE_CODE_ES_US),
    [vocabularyDefinitions]
  );

  const years = useMemo(() => {
    if (contentType === LESSON_ACTIVITIES) {
      return getInteractiveContent(contentId, contentType)?.lesson?.years;
    } else {
      return getInteractiveAssessmentContent(contentId)?.assessment?.years;
    }
  }, [
    contentId,
    contentType,
    getInteractiveContent,
    getInteractiveAssessmentContent,
  ]);

  const { isVisible, openModal, closeModal } = useSimpleModalState();

  return (
    <>
      {isVisible && vocabularyDefinitions?.[indexRef.current] && (
        <VocabularyViewModal
          definition={vocabularyDefinitions[indexRef.current]}
          years={years}
          closeModal={closeModal}
          contentId={contentId}
          sectionId={sectionId}
          contentType={contentType}
          componentId={componentId}
          isStudent={isStudent}
        />
      )}
      <Styled.VocabularyTitle>Vocabulary</Styled.VocabularyTitle>
      {isRegionUS && includeToggle && (
        <Styled.VocabularyExtraButton>
          <LessonSpanishSwitch
            isSpanishSupportAvailable={isSpanishSupported}
            sectionId={sectionId}
            contentId={contentId}
            contentType={contentType}
            componentId={componentId}
          />
        </Styled.VocabularyExtraButton>
      )}
      <Styled.VocabularyList data-selector="vocab-list">
        {vocabularyDefinitions?.map((def, index) => (
          <Styled.VocabularyListItem key={index} data-selector="vocab-item">
            <Styled.VocabularyButton
              type="button"
              aria-haspopup="dialog"
              aria-label={`Click to view definition for ${def.word}`}
              data-testid={`vocabulary-button-${index}`}
              onClick={() => {
                indexRef.current = index;
                onComponentExpand?.({
                  vocabularyWord: def.word,
                  vocabularyWordCount: vocabularyDefinitions.length,
                });
                openModal();
              }}
            >
              {def.word}
            </Styled.VocabularyButton>
          </Styled.VocabularyListItem>
        ))}
      </Styled.VocabularyList>
    </>
  );
};

export default VocabularyView;
